import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { getWorkOrder, deleteWorkOrder, updateWorkOrder } from '../services/maintenanceService';
import WorkOrderPhotos from '../components/WorkOrderPhotos';
import { useLocationContext } from '../contexts/LocationContext';

const MaintenanceWorkOrderDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentLocation } = useLocationContext();
  const [workOrder, setWorkOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchWorkOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentLocation?.id]);

  const fetchWorkOrder = async () => {
    try {
      setLoading(true);
      const data = await getWorkOrder(id);
      setWorkOrder(data);
    } catch (err) {
      setError('Failed to load work order');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this work order? This cannot be undone.')) return;
    try {
      await deleteWorkOrder(id);
      navigate('/maintenance');
    } catch (err) {
      setError('Failed to delete work order');
    }
  };

  const handleMarkComplete = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      await updateWorkOrder(id, { status: 'completed', completed_date: today });
      navigate('/maintenance');
    } catch (err) {
      setError('Failed to mark work order as complete');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T12:00:00').toLocaleDateString();
  };

  const priorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'normal': return 'bg-blue-100 text-blue-800';
      case 'low': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const statusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'open': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const statusLabel = (status) => {
    switch (status) {
      case 'in_progress': return 'In Progress';
      case 'open': return 'Open';
      case 'completed': return 'Completed';
      default: return status;
    }
  };

  const typeLabel = (type) => {
    switch (type) {
      case 'repair': return 'Repair';
      case 'scheduled': return 'Scheduled';
      case 'task': return 'Task';
      case 'capex': return 'Capex';
      default: return type;
    }
  };

  const typeColor = (type) => {
    switch (type) {
      case 'capex': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!workOrder) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Work order not found.</p>
        <Link to="/maintenance" className="text-blue-600 hover:text-blue-800 mt-2 inline-block">
          Back to Maintenance
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex justify-between items-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{workOrder.title}</h1>
          <div className="hidden sm:flex space-x-3">
            {workOrder.status !== 'completed' && (
              <button
                onClick={handleMarkComplete}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Mark Complete
              </button>
            )}
            <Link
              to={`/maintenance/work-order/edit/${id}`}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Edit
            </Link>
            <button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Delete
            </button>
            <button
              onClick={() => navigate('/maintenance')}
              className="text-gray-600 hover:text-gray-800 text-sm font-medium px-4 py-2"
            >
              Back
            </button>
          </div>
        </div>
        <div className="sm:hidden flex space-x-2 mt-3">
          {workOrder.status !== 'completed' && (
            <button
              onClick={handleMarkComplete}
              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
            >
              Mark Complete
            </button>
          )}
          <Link
            to={`/maintenance/work-order/edit/${id}`}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
          >
            Edit
          </Link>
          <button
            onClick={handleDelete}
            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
          >
            Delete
          </button>
          <button
            onClick={() => navigate('/maintenance')}
            className="text-gray-600 hover:text-gray-800 text-xs font-medium px-3 py-1.5"
          >
            Back
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center space-x-3">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${statusColor(workOrder.status)}`}>
            {statusLabel(workOrder.status)}
          </span>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${priorityColor(workOrder.priority)}`}>
            {workOrder.priority}
          </span>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${typeColor(workOrder.type)}`}>
            {typeLabel(workOrder.type)}
          </span>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Equipment Area</h3>
              <p className="mt-1 text-sm text-gray-900">{workOrder.equipment_area?.name || (workOrder.equipment_category?.length > 0 ? workOrder.equipment_category.join(', ') : 'General')}</p>
            </div>
            <div>
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</h3>
              <p className="mt-1 text-sm text-gray-900">{formatDate(workOrder.due_date)}</p>
            </div>
            <div>
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Completed Date</h3>
              <p className="mt-1 text-sm text-gray-900">{formatDate(workOrder.completed_date)}</p>
            </div>
            <div>
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Created</h3>
              <p className="mt-1 text-sm text-gray-900">{new Date(workOrder.created_at).toLocaleString()}</p>
            </div>
          </div>

          {workOrder.description && (
            <div>
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Description</h3>
              <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{workOrder.description}</p>
            </div>
          )}

          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Cost & Work Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Parts Cost</p>
                <p className="text-lg font-semibold text-gray-900">${(workOrder.parts_cost || 0).toFixed(2)}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Labor Cost</p>
                <p className="text-lg font-semibold text-gray-900">${(workOrder.labor_cost || 0).toFixed(2)}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-xs text-blue-600">Total Cost</p>
                <p className="text-lg font-semibold text-blue-900">${(workOrder.total_cost || 0).toFixed(2)}</p>
              </div>
            </div>
          </div>

          {(workOrder.vendor || workOrder.performed_by || workOrder.assigned_to) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {workOrder.vendor && (
                <div>
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</h3>
                  <p className="mt-1 text-sm text-gray-900">{workOrder.vendor}</p>
                </div>
              )}
              {workOrder.assigned_to ? (
                <div>
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</h3>
                  <p className="mt-1 text-sm text-gray-900">{workOrder.assigned_to.display_name}</p>
                </div>
              ) : workOrder.performed_by ? (
                <div>
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Performed By</h3>
                  <p className="mt-1 text-sm text-gray-900">{workOrder.performed_by}</p>
                </div>
              ) : null}
            </div>
          )}

        </div>
      </div>

      <WorkOrderPhotos workOrderId={id} />
    </div>
  );
};

export default MaintenanceWorkOrderDetail;
